import type {Store} from "@/components/pages/Modules/DoubleMateriality/store";
import {
    calculateFinal, calculateFinalMateriality,
    calculateIroImpactSurveysRating, calculateIroRiskAndOpportunitySurveysRating,
    calculateMaterialityForImpact, calculateMaterialityForRiskAndOpportunity,
    calculateSeverityForImpact,
    calculateTotalScoreForImpact, calculateTotalScoreForRiskAndOpportunity
} from "@/components/pages/Modules/DoubleMateriality/utils";
import {notEmpty} from "@/utils/helpers/notEmpty";
import type {
    FinalMaterialityRowItem,
    OgSurveysRowItem
} from "@/components/pages/Modules/DoubleMateriality/PgResults/PgList/types";
import orderBy from "lodash/orderBy";

export const finalMaterialityHeaders = [
  { text: 'Standard', value: 'esrs', columnClasses: 'w-[200px]' },
  { text: 'Sustainability matter', value: 'sustainabilityMatter' },
  { text: 'IRO', value: 'iro' },
  { text: 'IRO type', value: 'iroType', columnClasses: 'w-[215px]' },
  { text: 'Internal', value: 'internal', columnClasses: 'w-[150px]' },
  {
    text: 'Stakeholders',
    value: 'stakeholders',
    columnClasses: 'w-[150px]',
  },
  { text: 'Final', value: 'final', columnClasses: 'w-[150px]' },
  { text: 'Materiality', value: 'materiality', columnClasses: 'w-[100px]' },
];

export const impactHeaders = [
  { text: 'Standard', value: 'esrs', columnClasses: 'w-[200px]' },
  { text: 'Sustainability matter', value: 'sustainabilityMatter' },
  { text: 'IRO', value: 'iro' },
  { text: 'IRO Type', value: 'iroType', columnClasses: 'w-[215px]' },
  { text: 'Severity', value: 'severity', columnClasses: 'w-[150px]' },
  { text: 'Likelihood', value: 'likelihood', columnClasses: 'w-[150px]' },
  { text: 'Total Score', value: 'totalScore', columnClasses: 'w-[150px]' },
  { text: 'Materiality', value: 'materiality', columnClasses: 'w-[100px]' },
];

export const surveyHeaders = [
  { text: 'Standard', value: 'esrs', columnClasses: 'w-[200px]' },
  { text: 'Sustainability matter', value: 'sustainabilityMatter' },
  { text: 'IRO', value: 'iro' },
  { text: 'IRO type', value: 'iroType', columnClasses: 'w-[665px]' },
  { text: 'Total score', value: 'totalScore', columnClasses: 'w-[100px]' },
];

export const risksAndOpportunitiesHeaders = [
  { text: 'Standard', value: 'esrs', columnClasses: 'w-[200px]' },
  { text: 'Sustainability matter', value: 'sustainabilityMatter' },
  { text: 'IRO', value: 'iro' },
  { text: 'IRO type', value: 'iroType' },
  { text: 'Likelihood', value: 'likelihood', columnClasses: 'w-[150px]' },
  {
    text: 'Potential magnitude',
    value: 'potentialMagnitude',
    columnClasses: 'w-[150px]',
  },
  { text: 'Total score', value: 'totalScore', columnClasses: 'w-[150px]' },
  { text: 'Materiality', value: 'materiality', columnClasses: 'w-[100px]' },
];

export const getImpactsData = (store: Store) =>
  store.iroImpacts
    .map((iroImpact) => {
      const topic = store.topics.find(
        (item) => item._id === iroImpact.topicId,
      )!;

      if (!topic.topic || !topic.subTopic) {
        return null;
      }

      const severity = calculateSeverityForImpact(
        iroImpact.scale,
        iroImpact.scope,
        iroImpact.irremediability,
        iroImpact.type,
      );
      const totalScore = calculateTotalScoreForImpact(
        severity,
        iroImpact.likelihood,
        iroImpact.type,
      );
      const materiality = calculateMaterialityForImpact(
        iroImpact.scale,
        iroImpact.scope,
        iroImpact.irremediability,
        iroImpact.likelihood,
        iroImpact.type,
      );

      return {
        _id: iroImpact._id,
        topic,
        iroImpact,
        severity,
        likelihood: iroImpact.likelihood,
        totalScore,
        materiality,
      };
    })
    .filter((item) => notEmpty(item));

export const getSurveysData = (store: Store) => {
    const impacts: OgSurveysRowItem[] = store.iroImpacts
        .map((iroImpact) => {
            const topic = store.topics.find(
                (item) => item._id === iroImpact.topicId,
            )!;

            if (!topic.topic || !topic.subTopic) {
                return null;
            }

            const totalScore = calculateIroImpactSurveysRating(
                iroImpact._id,
                store.surveys,
            );

            return {
                _id: iroImpact._id,
                topic,
                iroImpact,
                iroRiskAndOpportunity: null,
                totalScore,
            };
        })
        .filter((item) => notEmpty(item));

    const risksAndOpportunities: OgSurveysRowItem[] =
        store.iroRisksAndOpportunities
            .map((iroRiskAndOpportunity) => {
                const topic = store.topics.find(
                    (item) => item._id === iroRiskAndOpportunity.topicId,
                )!;

                if (!topic.topic || !topic.subTopic) {
                    return null;
                }

                const totalScore = calculateIroRiskAndOpportunitySurveysRating(
                    iroRiskAndOpportunity._id,
                    store.surveys,
                );

                return {
                    _id: iroRiskAndOpportunity._id,
                    topic,
                    iroImpact: null,
                    iroRiskAndOpportunity,
                    totalScore,
                };
            })
            .filter((item) => notEmpty(item));

    return orderBy(
        [...impacts, ...risksAndOpportunities],
        [(item: OgSurveysRowItem) => item.topic.esrs],
        ['asc'],
    );
}

export const getRisksAndOpportunitiesData = (store: Store) => {
    return store.iroRisksAndOpportunities
        .map((iroRiskAndOpportunity) => {
            const topic = store.topics.find(
                (item) => item._id === iroRiskAndOpportunity.topicId,
            )!;

            if (!topic.topic || !topic.subTopic) {
                return null;
            }

            const totalScore = calculateTotalScoreForRiskAndOpportunity(
                iroRiskAndOpportunity.likelihood,
                iroRiskAndOpportunity.potentialMagnitude,
            );
            const materiality = calculateMaterialityForRiskAndOpportunity(
                iroRiskAndOpportunity.likelihood,
                iroRiskAndOpportunity.potentialMagnitude,
            );

            return {
                _id: iroRiskAndOpportunity._id,
                topic,
                iroRiskAndOpportunity,
                likelihood: iroRiskAndOpportunity.likelihood,
                potentialMagnitude: iroRiskAndOpportunity.potentialMagnitude,
                totalScore,
                materiality,
            };
        })
        .filter((item) => notEmpty(item));
}

export const getFinalMaterialityData = (store: Store) => {
    const impacts: FinalMaterialityRowItem[] = store.iroImpacts
        .map((iroImpact) => {
            const topic = store.topics.find(
                (item) => item._id === iroImpact.topicId,
            )!;

            if (!topic.topic || !topic.subTopic) {
                return null;
            }

            const severity = calculateSeverityForImpact(
                iroImpact.scale,
                iroImpact.scope,
                iroImpact.irremediability,
                iroImpact.type,
            );
            const internal = calculateTotalScoreForImpact(
                severity,
                iroImpact.likelihood,
                iroImpact.type,
            );
            const stakeholders = calculateIroImpactSurveysRating(
                iroImpact._id,
                store.surveys,
            );
            const final = calculateFinal(
                internal,
                stakeholders,
                store.resultCalculation,
            );
            const materiality = calculateFinalMateriality(final);

            return {
                _id: iroImpact._id,
                topic,
                iroImpact,
                iroRiskAndOpportunity: null,
                internal,
                stakeholders,
                final,
                materiality,
            };
        })
        .filter((item) => notEmpty(item));

    const risksAndOpportunities: FinalMaterialityRowItem[] =
        store.iroRisksAndOpportunities
            .map((iroRiskAndOpportunity) => {
                const topic = store.topics.find(
                    (item) => item._id === iroRiskAndOpportunity.topicId,
                )!;

                if (!topic.topic || !topic.subTopic) {
                    return null;
                }

                const internal = calculateTotalScoreForRiskAndOpportunity(
                    iroRiskAndOpportunity.likelihood,
                    iroRiskAndOpportunity.potentialMagnitude,
                );
                const stakeholders = calculateIroRiskAndOpportunitySurveysRating(
                    iroRiskAndOpportunity._id,
                    store.surveys,
                );
                const final = calculateFinal(
                    internal,
                    stakeholders,
                    store.resultCalculation,
                );
                const materiality = calculateFinalMateriality(final);

                return {
                    _id: iroRiskAndOpportunity._id,
                    topic,
                    iroImpact: null,
                    iroRiskAndOpportunity,
                    internal,
                    stakeholders,
                    final,
                    materiality,
                };
            })
            .filter((item) => notEmpty(item));

    return orderBy(
        [...impacts, ...risksAndOpportunities],
        [(item: FinalMaterialityRowItem) => item.topic.esrs],
        ['asc'],
    );
}
